import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Driver } from 'driver.js'
import { RootState } from 'store/modules/rootReducer'
import { markWizardStepSeen, dismissWizardTour, ITourWizardState } from 'store/modules/tour'
import {
  getWizardStep1Config,
  getWizardStep2Config,
  getWizardStep3Config,
  getWizardStep4Config,
  createDriverInstance,
} from '../utils/tourConfig'
import {
  trackWizardTourShown,
  trackWizardTourSkipped,
  trackWizardStepCompleted,
} from '../utils/tourTracking'
import { useTracking } from 'react-tracking'

interface UseWizardTourProps {
  currentStep: number
  isDarkMode: boolean
  mode: 'view' | 'create' | 'edit'
  hasPlans: boolean
}

export const useWizardTour = ({ currentStep, isDarkMode, mode, hasPlans }: UseWizardTourProps) => {
  const dispatch = useDispatch()
  const { trackEvent } = useTracking()
  const driverRef = useRef<Driver | null>(null)
  const tourState = useSelector((state: RootState) => state.tour.wizard)

  // Driver instance will be created per tour in startTourForStep

  // Check if element exists
  const elementExists = useCallback((selector: string): boolean => {
    try {
      const element = document.querySelector(selector)
      return element !== null
    } catch {
      return false
    }
  }, [])

  // Filter steps that have existing elements
  const filterValidSteps = useCallback(
    (steps: ReturnType<typeof getWizardStep1Config>) => {
      return steps.filter((step) => elementExists(step.element))
    },
    [elementExists]
  )

  // Start tour for a specific step
  const startTourForStep = useCallback(
    (step: 1 | 2 | 3 | 4, force = false) => {
      const stepKey = `step_${step}` as keyof ITourWizardState
      const hasSeenStep = tourState[stepKey] as boolean

      console.log('hasSeenStep', hasSeenStep)
      console.log('tourState.dismissed', tourState.dismissed)
      console.log('force', force)
      console.log('mode', mode)
      console.log('hasPlans', hasPlans)
      // Don't show if already seen or dismissed (unless forced)
      if (!force && (hasSeenStep || tourState.dismissed)) return

      // Don't show in edit mode or if has plans (unless forced)
      if (!force && (mode === 'edit' || hasPlans)) return

      let config: ReturnType<typeof getWizardStep1Config>
      switch (step) {
        case 1:
          config = getWizardStep1Config(isDarkMode)
          break
        case 2:
          config = getWizardStep2Config(isDarkMode)
          break
        case 3:
          config = getWizardStep3Config(isDarkMode)
          break
        case 4:
          config = getWizardStep4Config(isDarkMode)
          break
        default:
          return
      }

      const validSteps = filterValidSteps(config)

      if (validSteps.length === 0) {
        // No valid elements, mark as seen and skip
        dispatch(markWizardStepSeen(step))
        return
      }

      // Track tour shown
      trackEvent(trackWizardTourShown(step))

      // Create a new driver instance with onDestroyed callback
      const handleTourComplete = () => {
        // Mark step as seen when tour completes
        dispatch(markWizardStepSeen(step))
        trackEvent(trackWizardStepCompleted(step))
      }

      const driverInstance = createDriverInstance(isDarkMode, handleTourComplete)

      // Configure driver with steps
      driverInstance.setSteps(
        validSteps.map((stepConfig) => ({
          element: stepConfig.element,
          popover: {
            title: stepConfig.popover.title,
            description: stepConfig.popover.description,
            side: stepConfig.popover.side || 'bottom',
            align: stepConfig.popover.align || 'center',
          },
        }))
      )

      // Store the instance
      driverRef.current = driverInstance

      // Start tour with a small delay to ensure DOM is ready
      setTimeout(() => {
        if (driverRef.current) {
          try {
            driverRef.current.drive()
          } catch (error) {
            console.error('Error starting wizard tour:', error)
            // Mark as seen on error to prevent retry loops
            dispatch(markWizardStepSeen(step))
          }
        }
      }, 300)
    },
    [isDarkMode, mode, hasPlans, tourState, dispatch, trackEvent, filterValidSteps]
  )

  // Skip tour
  const skipTour = useCallback(
    (step: 1 | 2 | 3 | 4) => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
      dispatch(dismissWizardTour())
      trackEvent(trackWizardTourSkipped(step))
    },
    [dispatch, trackEvent]
  )

  // Auto-start tour when step changes or when entering create mode
  useEffect(() => {
    if (mode === 'create' && !hasPlans && !tourState.dismissed) {
      const stepKey = `step_${currentStep}` as keyof ITourWizardState
      const hasSeenStep = tourState[stepKey] as boolean

      // Debug logs (remove after testing)
      console.log('[Tour Debug]', {
        mode,
        hasPlans,
        dismissed: tourState.dismissed,
        currentStep,
        stepKey,
        hasSeenStep,
        tourState,
      })

      if (!hasSeenStep && currentStep >= 1 && currentStep <= 4) {
        // Wait for DOM to be ready - check if elements exist
        const checkElements = () => {
          // Check if at least one element from the current step exists
          let config: ReturnType<typeof getWizardStep1Config>
          switch (currentStep) {
            case 1:
              config = getWizardStep1Config(isDarkMode)
              break
            case 2:
              config = getWizardStep2Config(isDarkMode)
              break
            case 3:
              config = getWizardStep3Config(isDarkMode)
              break
            case 4:
              config = getWizardStep4Config(isDarkMode)
              break
            default:
              return false
          }

          // Check if at least one element exists
          const hasValidElement = config.some((stepConfig) => {
            try {
              return document.querySelector(stepConfig.element) !== null
            } catch {
              return false
            }
          })

          return hasValidElement
        }

        // Try to start tour, with retries if elements aren't ready
        let attempts = 0
        const maxAttempts = 10
        const attemptStart = () => {
          attempts++
          if (checkElements()) {
            startTourForStep(currentStep as 1 | 2 | 3 | 4)
          } else if (attempts < maxAttempts) {
            setTimeout(attemptStart, 300)
          } else {
            // Elements still not found after max attempts, mark as seen to avoid infinite retries
            console.warn(
              `Tour step ${currentStep}: Elements not found after ${maxAttempts} attempts`
            )
            dispatch(markWizardStepSeen(currentStep as 1 | 2 | 3 | 4))
          }
        }

        // Start checking after initial delay
        const timer = setTimeout(attemptStart, 500)

        return () => clearTimeout(timer)
      }
    }
  }, [currentStep, mode, hasPlans, tourState, startTourForStep, isDarkMode, dispatch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return {
    startTourForStep,
    skipTour,
  }
}
